import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { FileText, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

export const TermsOfServicePage = () => {
  const lastUpdated = "February 5, 2025";
  
  return (
    <div className="min-h-screen bg-[#09090B]">
      <div className="noise-overlay" />
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <Link to="/register" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Registration
            </Link>
            
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-3xl sm:text-4xl uppercase text-white">
                  Terms of Service
                </h1>
                <p className="text-zinc-500 text-sm">Last updated: {lastUpdated}</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="stat-card p-8 space-y-8">
            <section>
              <h2 className="text-xl font-bold text-white mb-4">1. Acceptance of Terms</h2>
              <p className="text-zinc-400 leading-relaxed">
                By accessing or using Slab Society ("the Platform"), you agree to be bound by these Terms of Service 
                and all applicable laws and regulations. If you do not agree with any of these terms, you are 
                prohibited from using or accessing this site.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4">2. Description of Service</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                Slab Society is an online marketplace for buying and selling graded sports cards and collectibles. 
                Our platform connects buyers and sellers, providing tools for listing, purchasing, and managing 
                transactions.
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Sellers pay a monthly subscription fee of $20.00 to list items</li>
                <li>Sellers keep 100% of their sale price (0% commission)</li>
                <li>Buyers pay a 3.5% transaction fee on all purchases</li>
                <li>Buyers are responsible for all shipping costs and applicable taxes</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4">3. User Accounts</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                To access certain features of the Platform, you must register for an account. You agree to:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Provide accurate, current, and complete information during registration</li>
                <li>Maintain the security of your password and accept all risks of unauthorized access</li>
                <li>Promptly notify us if you discover or suspect any security breaches</li>
                <li>Take responsibility for all activities that occur under your account</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4">4. Seller Responsibilities</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                As a seller on Slab Society, you agree to:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>List only authentic items that you own and have the right to sell</li>
                <li>Provide accurate descriptions and images of your items</li>
                <li>Ship items within 3 business days of receiving payment</li>
                <li>Package items securely to prevent damage during transit</li>
                <li>Respond to buyer inquiries within 48 hours</li>
                <li>Honor all completed sales at the listed price</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4">5. Buyer Responsibilities</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                As a buyer on Slab Society, you agree to:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Pay for items promptly upon completing a purchase</li>
                <li>Provide accurate shipping information</li>
                <li>Inspect items upon receipt and report any issues within 48 hours</li>
                <li>Leave honest feedback based on your transaction experience</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4">6. Prohibited Activities</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                Users are prohibited from:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Listing counterfeit, stolen, or misrepresented items</li>
                <li>Manipulating reviews or ratings</li>
                <li>Engaging in fraudulent transactions</li>
                <li>Harassing or threatening other users</li>
                <li>Circumventing platform fees by completing transactions off-platform</li>
                <li>Using the platform for any illegal purposes</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4">7. Fees and Payments</h2>
              <p className="text-zinc-400 leading-relaxed">
                Seller subscriptions are billed monthly at $20.00 and may be cancelled at any time. 
                Buyer transaction fees of 3.5% are calculated on the item price and are non-refundable 
                once a transaction is completed. All payments are processed securely through Stripe.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4">8. Dispute Resolution</h2>
              <p className="text-zinc-400 leading-relaxed">
                In the event of a dispute between buyers and sellers, both parties agree to first 
                attempt resolution through our customer support team. We reserve the right to make 
                final decisions on disputes and may, at our discretion, issue refunds or take other 
                actions we deem appropriate.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4">9. Limitation of Liability</h2>
              <p className="text-zinc-400 leading-relaxed">
                Slab Society acts as a marketplace platform and is not responsible for the quality, 
                authenticity, or legality of items listed by sellers. We make no warranties regarding 
                the outcome of any transaction. Our liability is limited to the fees paid to us for 
                the transaction in question.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4">10. Modifications to Terms</h2>
              <p className="text-zinc-400 leading-relaxed">
                We reserve the right to modify these Terms of Service at any time. Changes will be 
                effective immediately upon posting to the Platform. Your continued use of the Platform 
                after any changes constitutes acceptance of the new terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-4">11. Contact Information</h2>
              <p className="text-zinc-400 leading-relaxed">
                For questions about these Terms of Service, please contact us at:
              </p>
              <div className="mt-4 p-4 rounded-lg bg-zinc-900/50 border border-white/5">
                <p className="text-white font-medium">Slab Society</p>
                <p className="text-zinc-400">Founded by Ron Manthei</p>
                <p className="text-zinc-400">Kansas City, Missouri</p>
                <p className="text-blue-400">support@slabsociety.com</p>
              </div>
            </section>
          </div>

          {/* Footer CTA */}
          <div className="mt-8 text-center">
            <Link to="/register">
              <Button className="btn-primary" data-testid="tos-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Registration
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img 
              src={process.env.REACT_APP_LOGO_URL} 
              alt="Slab Society"
              className="w-8 h-8 rounded-lg object-contain"
            />
            <span className="text-zinc-500">© 2024 Slab Society. Founded by Ron Manthei</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-zinc-500">
            <Link to="/marketplace" className="hover:text-white transition-colors">Marketplace</Link>
            <Link to="/about" className="hover:text-white transition-colors">About Us</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfServicePage;
